import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ColumnConfig,
  ColumnDefinition,
  TableLayoutConfig,
  UseTableConfigReturn,
} from './types';

/**
 * Hook for managing table column configuration state.
 * Handles visibility, ordering, widths, and custom labels.
 *
 * Pure UI state — persystencja siedzi w useTableLayout (zapisuje config
 * z tego hooka do `list_config` w bazie przez PUT endpointów).
 *
 * Zmiana ZAPISANEGO UKŁADU (`savedConfig`, np. przełączenie layoutu) inicjalizuje konfigurację kolumn
 * od nowa. Zmiana SAMYCH `columns` (nowa tablica, np. po doczytaniu opcji filtra albo firm, które
 * wchodzą do definicji kolumn) NIE zeruje konfiguracji: kolumny o znanym id zachowują widoczność,
 * szerokość, etykietę i kolejność ustawione przez użytkownika (kolumna NIETKNIĘTA — jej ustawienia
 * to jeszcze domyślne z poprzedniej definicji — podąża za domyślnymi nowej, np. `defaultVisible`
 * zależne od aktywnego modułu), nowe dostają konfigurację wg tych samych reguł co przy
 * inicjalizacji, a zniknięte są usuwane (`mergeColumnConfigs`).
 */
export function useTableConfig<T>(
  columns: ColumnDefinition<T>[],
  savedConfig?: TableLayoutConfig | null,
): UseTableConfigReturn {
  // Initialize column configs from saved config or defaults
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(() =>
    initializeColumnConfigs(columns, savedConfig),
  );

  // Poprzednie wejścia efektu — odróżniają zmianę zapisanego układu (inicjalizacja od nowa) od
  // zmiany samych kolumn (scalenie z tym, co ustawił użytkownik).
  const previousSavedConfigRef = useRef(savedConfig);
  const previousColumnsRef = useRef(columns);

  // Update configs when saved config changes (e.g., after switching layouts); gdy zmieniły się tylko
  // kolumny — scal. Bez zapisanego układu efekt niczego nie robi (jak dotąd).
  useEffect(() => {
    const previousColumns = previousColumnsRef.current;
    const savedConfigChanged = previousSavedConfigRef.current !== savedConfig;
    const columnsChanged = previousColumns !== columns;
    previousSavedConfigRef.current = savedConfig;
    previousColumnsRef.current = columns;
    if (!savedConfig) return;
    if (savedConfigChanged) {
      setColumnConfigs(initializeColumnConfigs(columns, savedConfig));
    } else if (columnsChanged) {
      setColumnConfigs((previous) =>
        mergeColumnConfigs(previous, previousColumns, columns, savedConfig),
      );
    }
  }, [savedConfig, columns]);

  // Calculate visible columns based on current config
  const visibleColumns = useMemo(() => {
    const configMap = new Map(columnConfigs.map((c) => [c.id, c]));

    return columns
      .filter((col) => {
        const config = configMap.get(col.id);
        return config?.visible ?? col.defaultVisible ?? true;
      })
      .sort((a, b) => {
        const configA = configMap.get(a.id);
        const configB = configMap.get(b.id);
        return (configA?.order ?? 0) - (configB?.order ?? 0);
      });
  }, [columns, columnConfigs]);

  // Reorder columns
  const reorderColumns = useCallback((sourceIndex: number, destinationIndex: number) => {
    setColumnConfigs((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const [removed] = sorted.splice(sourceIndex, 1);
      if (!removed) return prev;
      sorted.splice(destinationIndex, 0, removed);

      // Reassign order values
      return sorted.map((config, index) => ({
        ...config,
        order: index,
      }));
    });
  }, []);

  // Toggle column visibility
  const toggleColumnVisibility = useCallback((columnId: string) => {
    setColumnConfigs((prev) =>
      prev.map((config) =>
        config.id === columnId ? { ...config, visible: !config.visible } : config,
      ),
    );
  }, []);

  // Update column width
  const updateColumnWidth = useCallback((columnId: string, width: number | string) => {
    setColumnConfigs((prev) =>
      prev.map((config) => (config.id === columnId ? { ...config, width } : config)),
    );
  }, []);

  // Update column custom label
  const updateColumnLabel = useCallback((columnId: string, label: string | undefined) => {
    setColumnConfigs((prev) =>
      prev.map((config) =>
        config.id === columnId ? { ...config, customLabel: label || undefined } : config,
      ),
    );
  }, []);

  // Reset to default configuration
  const resetColumnConfig = useCallback(() => {
    setColumnConfigs(initializeColumnConfigs(columns, null));
  }, [columns]);

  return {
    columnConfigs,
    setColumnConfigs,
    visibleColumns: visibleColumns as ColumnDefinition[],
    reorderColumns,
    toggleColumnVisibility,
    updateColumnWidth,
    updateColumnLabel,
    resetColumnConfig,
  };
}

/** Kolumny zapisanego układu; puste = brak układu (obowiązują domyślne ustawienia kolumn). */
function savedLayoutColumns(savedConfig?: TableLayoutConfig | null): ColumnConfig[] {
  return savedConfig?.columns && savedConfig.columns.length > 0 ? savedConfig.columns : [];
}

/**
 * Konfiguracja JEDNEJ kolumny, której jeszcze nie ma w bieżącym stanie — wspólna reguła dla
 * inicjalizacji i dla kolumn dochodzących przy scalaniu (`mergeColumnConfigs`):
 *  - zapisany układ ma kolumny (`layoutColumns` niepuste): `hideable: false` jest zawsze widoczna,
 *    kolumna z wpisem w układzie bierze wpis, kolumna spoza układu jest UKRYTA;
 *  - brak układu: widoczność z `defaultVisible`.
 * `order` podaje wołający (pozycja w inicjalizacji albo za istniejącymi przy scalaniu).
 */
function newColumnConfig<T>(
  col: ColumnDefinition<T>,
  saved: ColumnConfig | undefined,
  hasLayout: boolean,
  order: number,
): ColumnConfig {
  if (hasLayout) {
    // `hideable: false` wygrywa zawsze — „nie da się ukryć" musi znaczyć „nigdy nie ukryta",
    // nie tylko „nie da się odznaczyć w kreatorze idąc od teraz". Bez tego zapisany układ
    // sprzed dodania takiej kolumny (np. przed migracją kartoteki na silnik list) chowa ją
    // na zawsze — brak kolumny identyfikującej wiersz (2026-09-07, lustro w
    // `computeVisibleColumnParams`, ext-list-columns.tsx).
    if (col.hideable === false) {
      return {
        id: col.id,
        visible: true,
        width: saved?.width ?? col.defaultWidth ?? 'auto',
        customLabel: saved?.customLabel,
        order: saved?.order ?? order,
      };
    }
    if (saved) {
      return saved;
    }
    // Kolumna spoza zapisanego układu — UKRYTA. Układ jest dosłowny: nie wpychamy nowych
    // pól „na chama" do istniejących układów (user dodaje je świadomie w kreatorze). Dokładana
    // na koniec, żeby po ręcznym odsłonięciu miała stabilną pozycję. Bez układu (gałąź niżej)
    // obowiązuje `defaultVisible` — brak układu = widać wszystko dostępne.
    return {
      id: col.id,
      visible: false,
      width: col.defaultWidth ?? 'auto',
      customLabel: undefined,
      order,
    };
  }

  // Default initialization from column definition
  return {
    id: col.id,
    visible: col.defaultVisible ?? true,
    width: col.defaultWidth ?? 'auto',
    customLabel: undefined,
    order,
  };
}

/**
 * Initialize column configs from saved config or column defaults
 */
function initializeColumnConfigs<T>(
  columns: ColumnDefinition<T>[],
  savedConfig?: TableLayoutConfig | null,
): ColumnConfig[] {
  const layoutColumns = savedLayoutColumns(savedConfig);
  if (layoutColumns.length > 0) {
    // Merge saved config with column definitions
    // Handle new columns that weren't in saved config
    const savedConfigMap = new Map(layoutColumns.map((c) => [c.id, c]));
    const maxOrder = Math.max(...layoutColumns.map((c) => c.order), -1);

    return columns.map((col, index) =>
      newColumnConfig(col, savedConfigMap.get(col.id), true, maxOrder + index + 1),
    );
  }

  // Default initialization from column definitions
  return columns.map((col, index) => newColumnConfig(col, undefined, false, index));
}

/** Czy dwie konfiguracje kolumny mają te same ustawienia widoczne dla użytkownika (bez kolejności). */
function sameColumnSettings(a: ColumnConfig, b: ColumnConfig): boolean {
  return a.visible === b.visible && a.width === b.width && a.customLabel === b.customLabel;
}

/**
 * Konfiguracja kolumny znanej już w stanie, po zmianie jej definicji:
 *  - ustawienia INNE niż domyślne z poprzedniej definicji (widoczność, szerokość, etykieta —
 *    zmiana użytkownika) zostają bez zmian,
 *  - ustawienia NIETKNIĘTE (równe domyślnym poprzedniej definicji) przechodzą na domyślne nowej —
 *    inaczej kolumna, której `defaultVisible` zależy od danych doczytanych po pierwszym renderze
 *    (np. kolumny procesu zależne od aktywnego modułu), zostałaby na wartości z klatki wczytywania.
 *    Kolejność (`order`) zawsze zostaje,
 *  - `hideable: false` jest zawsze widoczna.
 * Zwraca TĘ SAMĄ konfigurację, gdy nic się nie zmieniło.
 */
function refreshKnownColumnConfig<T>(
  config: ColumnConfig,
  column: ColumnDefinition<T>,
  previousColumn: ColumnDefinition<T> | undefined,
  saved: ColumnConfig | undefined,
  hasLayout: boolean,
): ColumnConfig {
  let next = config;
  if (previousColumn !== undefined) {
    const untouched = sameColumnSettings(
      config,
      newColumnConfig(previousColumn, saved, hasLayout, config.order),
    );
    if (untouched) {
      const fresh = newColumnConfig(column, saved, hasLayout, config.order);
      if (!sameColumnSettings(config, fresh)) next = { ...fresh, order: config.order };
    }
  }
  return column.hideable === false && !next.visible ? { ...next, visible: true } : next;
}

/**
 * Scala bieżącą konfigurację kolumn z nową tablicą kolumn (ten sam zapisany układ):
 *  - kolumny o znanym id zachowują ustawienia użytkownika (widoczność, szerokość, etykieta,
 *    kolejność); nietknięte podążają za domyślnymi nowej definicji, a `hideable: false` jest zawsze
 *    widoczna (`refreshKnownColumnConfig`),
 *  - kolumny nowe dostają konfigurację wg tych samych reguł co przy inicjalizacji
 *    (`newColumnConfig`), z kolejnością ZA istniejącymi (kolejność kolumn już ustawionych przez
 *    użytkownika się nie ruszy, a nowa nie koliduje z cudzym `order`),
 *  - kolumny, których już nie ma, są usuwane.
 * Kolejność tablicy: dotychczasowa (zachowane), potem nowe w kolejności `columns`. Gdy nic się nie
 * zmieniło, zwraca TĘ SAMĄ tablicę (bez zbędnego renderu).
 */
function mergeColumnConfigs<T>(
  previous: ColumnConfig[],
  previousColumns: ColumnDefinition<T>[],
  columns: ColumnDefinition<T>[],
  savedConfig: TableLayoutConfig,
): ColumnConfig[] {
  const columnById = new Map(columns.map((col) => [col.id, col]));
  const previousColumnById = new Map(previousColumns.map((col) => [col.id, col]));
  const layoutColumns = savedLayoutColumns(savedConfig);
  const hasLayout = layoutColumns.length > 0;
  const savedConfigMap = new Map(layoutColumns.map((c) => [c.id, c]));

  const kept = previous.flatMap((config): ColumnConfig[] => {
    const col = columnById.get(config.id);
    if (col === undefined) return [];
    return [
      refreshKnownColumnConfig(
        config,
        col,
        previousColumnById.get(config.id),
        savedConfigMap.get(config.id),
        hasLayout,
      ),
    ];
  });
  const keptIds = new Set(kept.map((config) => config.id));

  const maxOrder = Math.max(
    ...previous.map((c) => c.order),
    ...layoutColumns.map((c) => c.order),
    -1,
  );
  const added = columns.flatMap((col, index): ColumnConfig[] =>
    keptIds.has(col.id)
      ? []
      : [newColumnConfig(col, savedConfigMap.get(col.id), hasLayout, maxOrder + index + 1)],
  );

  const unchanged =
    added.length === 0 &&
    kept.length === previous.length &&
    kept.every((config, index) => config === previous[index]);
  return unchanged ? previous : [...kept, ...added];
}
